import React, { useRef, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/fluent-adapter';
import { Button, Alert, AlertDescription, RadioGroup, Checkbox, Label } from '@/fluent-adapter';
import {
  ArrowUpload24Regular as Upload,
  Dismiss24Regular as X,
  Warning24Regular as AlertTriangle,
  Attach24Regular as Attach,
  Shield24Regular as Shield,
} from '@fluentui/react-icons';
import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import type { FileUploadForm, Country, NDOForm } from '../../store/types';
import { MicrosoftEntityBanner } from '../MicrosoftEntityBanner/MicrosoftEntityBanner';
import { LEGAL_STRINGS } from '@/constants/legalStrings';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalSNudge,
  },
  title: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  dropZone: {
    ...shorthands.border('2px', 'dashed', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusLarge,
    textAlign: 'center',
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
    cursor: 'pointer',
  },
  dropZoneDragOver: {
    ...shorthands.border('2px', 'dashed', tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
  },
  uploadIconRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: tokens.spacingVerticalS,
  },
  hiddenInput: {
    display: 'none',
  },
  errorText: {
    color: tokens.colorStatusDangerForeground1,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalSNudge,
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  fileMeta: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    flex: 1,
    minWidth: 0,
  },
  fileIcon: {
    color: tokens.colorBrandForeground1,
  },
  fileName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileSize: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: tokens.spacingVerticalM,
  },
  summaryText: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  fileInfo: {
    flex: 1,
    minWidth: 0,
  },
  summarySize: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  overLimit: {
    color: tokens.colorStatusDangerForeground1,
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: tokens.spacingHorizontalS,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
});

interface FileUploadStepProps {
  formData: FileUploadForm;
  onFormChange: (data: Partial<FileUploadForm>) => void;
  onFileAdd: (file: File) => string;
  onFileRemove: (fileId: string) => void;
  onFileProgress?: (fileId: string, progress: number) => void;
  onFileUploaded?: (fileId: string, url: string) => void;
  onStartUpload?: (fileId: string, file: File) => Promise<string>;
  errors?: Record<string, string>;
  country?: Country | null;
  ndoData?: NDOForm;
  onNdoChange?: (data: Partial<NDOForm>) => void;
  isChildExploitation?: boolean;
  isPreservationRequest?: boolean;
}

const MIME_TYPE_NAMES: Record<string, string> = {
  'application/pdf': 'PDF',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
};

const FILE_VALIDATION = {
  minFileSize: 100,
  maxFileSize: 25 * 1024 * 1024, // 25MB per file
  maxTotalSize: 25 * 1024 * 1024, // 25MB total
  allowedMimeTypes: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  // Mirrors the server-side allow-list: the browser-supplied MIME type is easy to spoof and
  // is empty for some office files, so the extension is checked alongside it. The server
  // still has the final say by reading the file's actual contents.
  allowedExtensions: ['.pdf', '.doc', '.docx', '.xls', '.xlsx'],
  maxFiles: 20,
  minFiles: 1,
};

export const FileUploadStep: React.FC<FileUploadStepProps> = ({
  formData,
  onFileAdd,
  onFileRemove,
  errors = {},
  country = null,
  ndoData,
  onNdoChange,
  isChildExploitation = false,
  isPreservationRequest = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const styles = useStyles();

  // Calculate current total size
  const currentTotalSize = formData.files.reduce((total, file) => total + file.size, 0);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size < FILE_VALIDATION.minFileSize) {
        return `File "${file.name}" appears to be empty or invalid. Please upload a valid document with content.`;
      }

      // Check individual file size
      if (file.size > FILE_VALIDATION.maxFileSize) {
        return `File "${file.name}" exceeds the maximum size of ${FILE_VALIDATION.maxFileSize / 1024 / 1024}MB`;
      }

      // Check file type
      if (!FILE_VALIDATION.allowedMimeTypes.includes(file.type)) {
        const allowedTypes = FILE_VALIDATION.allowedMimeTypes
          .map((t) => MIME_TYPE_NAMES[t] || t)
          .join(', ');
        return `File type not allowed. Please upload ${allowedTypes} files`;
      }

      // Check extension as well: the MIME type is caller-supplied and sometimes blank, so a
      // mismatched or missing extension is rejected here to match the server's allow-list.
      const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      if (!file.name.includes('.') || !FILE_VALIDATION.allowedExtensions.includes(extension)) {
        return `File type not allowed. Please upload PDF, DOC, DOCX, XLS or XLSX files`;
      }

      // Per-file checks only. The count and running-total caps are batch concerns: a single
      // picker action can carry several files, and each would read the same pre-batch
      // formData here, so those are projected across the batch in handleFileSelect instead.
      return null;
    },
    []
  );

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setValidationError(null);

      const fileArray = Array.from(files);
      const validationErrors: string[] = [];

      // Project both the running count and the running total across the whole pick, starting
      // from what is already attached. Without the count projection, selecting more than
      // maxFiles files in one action would let every file past a check that only ever saw the
      // pre-batch count, bypassing the cap the server enforces (PORT-PARITY).
      let projectedTotalSize = currentTotalSize;
      let projectedCount = formData.files.length;

      fileArray.forEach((file) => {
        const error = validateFile(file);
        if (error) {
          validationErrors.push(error);
          return;
        }

        if (projectedCount >= FILE_VALIDATION.maxFiles) {
          validationErrors.push(`Maximum ${FILE_VALIDATION.maxFiles} files allowed`);
          return;
        }

        if (projectedTotalSize + file.size > FILE_VALIDATION.maxTotalSize) {
          validationErrors.push(`Adding "${file.name}" would exceed the total limit of ${FILE_VALIDATION.maxTotalSize / 1024 / 1024}MB`);
          return;
        }

        onFileAdd(file);
        projectedTotalSize += file.size;
        projectedCount += 1;
      });

      if (validationErrors.length > 0) {
        setValidationError(validationErrors[0]); // Show first error
      }
    },
    [validateFile, onFileAdd, currentTotalSize, formData.files.length]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = e.dataTransfer.files;
      if (!files || files.length === 0) {
        setValidationError('No files were detected. Please try again.');
        return;
      }
      handleFileSelect(files);
    },
    [handleFileSelect]
  );

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isOverTotalLimit = currentTotalSize > FILE_VALIDATION.maxTotalSize;

  return (
    <div className={styles.root}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Attach Supporting Documents</h2>
        <p className={styles.subtitle}>
          Attach all supporting documents for your data request
        </p>
      </div>

      {/* Microsoft Entity Banner */}
      {country && <MicrosoftEntityBanner country={country} />}

      {/* Hidden file input - placed outside dropzone to avoid nested interactive controls */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={FILE_VALIDATION.allowedMimeTypes.join(',')}
        onChange={(e) => {
          handleFileSelect(e.target.files);
          // Reset input so same file can be selected again
          e.target.value = '';
        }}
        className={styles.hiddenInput}
        aria-hidden="true"
      />

      {/* Drop Zone */}
      <Card>
        <CardHeader>
          <CardTitle>Attach Files</CardTitle>
          <CardDescription>Drag and drop files here, or click to browse</CardDescription>
        </CardHeader>
        <CardContent>
          <div
            id="file-dropzone"
            className={`${styles.dropZone} ${isDragOver ? styles.dropZoneDragOver : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            aria-label="Attach files by dropping them here or press Enter to browse"
            aria-describedby="file-dropzone-instructions"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <div className={styles.uploadIconRow}>
              <Upload />
            </div>
            <div>
              <p id="file-dropzone-instructions">
                Drop files here, or <span>browse files</span>
              </p>
              <p className={styles.subtitle}>
                Supports: PDF, DOC, DOCX, XLS, XLSX (max {FILE_VALIDATION.maxFileSize / 1024 / 1024}MB per file, {FILE_VALIDATION.maxTotalSize / 1024 / 1024}MB total)
              </p>
            </div>
          </div>

          {(errors.files || validationError) && (
            <p className={styles.errorText}>{errors.files || validationError}</p>
          )}
        </CardContent>
      </Card>

      {/* Attached Files */}
      {formData.files.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Attached Files ({formData.files.length})</CardTitle>
            <CardDescription>Review your attached files below</CardDescription>
          </CardHeader>
          <CardContent>
            <div className={styles.fileList}>
              {formData.files.map((file) => (
                <div key={file.id} className={styles.fileRow}>
                  <div className={styles.fileMeta}>
                    <Attach className={styles.fileIcon} />
                    <div className={styles.fileInfo}>
                      <p className={styles.fileName}>{file.name}</p>
                      <p className={styles.fileSize}>{formatFileSize(file.size)}</p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onFileRemove(file.id)}
                    aria-label={`Remove ${file.name}`}
                  >
                    <X />
                  </Button>
                </div>
              ))}
            </div>

            {/* Summary row */}
            <div className={styles.summaryRow}>
              <span className={styles.summaryText}>
                {formData.files.length} file{formData.files.length !== 1 ? 's' : ''} attached
              </span>
              <span className={`${styles.summarySize} ${isOverTotalLimit ? styles.overLimit : ''}`}>
                {formatFileSize(currentTotalSize)} / {FILE_VALIDATION.maxTotalSize / 1024 / 1024}MB
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Non-Disclosure Order (NDO) - Only for USA, non-preservation requests */}
      {(country?.code === 'USA' || country?.code === 'US' || country?.alphaThree === 'USA') && ndoData && onNdoChange && !isPreservationRequest && !isChildExploitation && (
        <Card>
          <CardHeader>
            <CardTitle>Non-Disclosure Order (NDO)</CardTitle>
            <CardDescription>
              Specify whether you are submitting a Non-Disclosure Order with this request
            </CardDescription>
          </CardHeader>
          <CardContent className={styles.cardContent}>
            <Alert>
              <Shield />
              <AlertDescription>
                <strong>Microsoft&apos;s Notification Policy:</strong> {LEGAL_STRINGS.USER_NOTIFICATION_POLICY}
              </AlertDescription>
            </Alert>

            <RadioGroup
              value={ndoData.hasNdo === null ? '' : ndoData.hasNdo.toString()}
              onValueChange={(value) => onNdoChange({ hasNdo: value === 'true' })}
              items={[
                { value: 'true', label: 'Yes, I am submitting an NDO' },
                { value: 'false', label: 'No, I am not submitting an NDO' },
              ]}
            />
            {errors.hasNdo && (
              <p className={styles.errorText}>{errors.hasNdo}</p>
            )}

            {ndoData.hasNdo === true && (
              <>
                <div className={styles.checkboxRow}>
                  <Checkbox
                    id="ndoConfirm"
                    checked={ndoData.isNdoConfirm === true}
                    onCheckedChange={(checked) => onNdoChange({ isNdoConfirm: !!checked })}
                  />
                  <Label htmlFor="ndoConfirm">
                    {LEGAL_STRINGS.NDO_CITATION_CHECKBOX}
                  </Label>
                </div>
                {errors.isNdoConfirm && (
                  <p className={styles.errorText}>{errors.isNdoConfirm}</p>
                )}
              </>
            )}

            {ndoData.hasNdo === false && (
              <>
                <Alert variant="destructive">
                  <AlertTriangle />
                  <AlertDescription>
                    <strong>WARNING:</strong> Without an NDO, Microsoft will notify the customer of this request.
                  </AlertDescription>
                </Alert>
                <div className={styles.checkboxRow}>
                  <Checkbox
                    id="ndoConfirmNotify"
                    checked={ndoData.isNdoConfirm === true}
                    onCheckedChange={(checked) => onNdoChange({ isNdoConfirm: !!checked })}
                  />
                  <Label htmlFor="ndoConfirmNotify">
                    {LEGAL_STRINGS.NDO_NO_NDO_CHECKBOX}
                  </Label>
                </div>
                {errors.isNdoConfirm && (
                  <p className={styles.errorText}>{errors.isNdoConfirm}</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
